import type { Marker, Cluster } from "@googlemaps/markerclusterer"
import * as d3 from "d3"
import { BIKE_MARKER_COLORS } from "../static/bike"
import { Bbox, Bounds } from "./map"
import {
  CLUSTER_MARKER_INNER_RADIUS,
  CLUSTER_MARKER_OUTER_RADIUS,
  CLUSTER_MARKER_PIE_SIZE,
  getStateModeColor,
} from "./clusters"

export interface LatLngObj {
  lat: number
  lng: number
}

export interface ZOptionsProps {
  zIndex?: number
}

export interface StrokeOptionsProps {
  strokeColor: string
  strokeOpacity?: number
  strokeWeight: number
}

export interface FillOptionsProps {
  fillColor: string
  fillOpacity: number
}

export type StyleOptionsProps = ZOptionsProps & FillOptionsProps & StrokeOptionsProps

export type GeoJsonLayerStyle = StyleOptionsProps | ((feature: google.maps.Data.Feature) => StyleOptionsProps)

export const createBBoxFromGoogleMapBounds = (bounds: google.maps.LatLngBounds): Bbox => {
  return [
    [bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
    [bounds.getNorthEast().lng(), bounds.getNorthEast().lat()],
  ]
}

export const createGoogleMapBoundsFromBBox = (bbox: Bbox): google.maps.LatLngBounds => {
  const sw = new google.maps.LatLng(bbox[0][1], bbox[0][0])
  const ne = new google.maps.LatLng(bbox[1][1], bbox[1][0])
  const gMapBounds = new google.maps.LatLngBounds(sw, ne)
  return gMapBounds
}

export const createGoogleMapsBoundsFromBounds = (bounds: Bounds): google.maps.LatLngBounds => {
  const sw = new google.maps.LatLng(bounds[0].lat, bounds[0].lng)
  const ne = new google.maps.LatLng(bounds[1].lat, bounds[1].lng)
  const gMapBounds = new google.maps.LatLngBounds(sw, ne)
  return gMapBounds
}

export const renderGoogleMapsCluster = (cluster: Cluster, theme: any): Marker => {
  const { markers, position, count } = cluster
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const markersStates = markers?.map((marker) => marker.assetState)
  const stateCounts: { [key: string]: number } = markersStates?.reduce((acc, state) => {
    acc[state] = acc[state] ? acc[state] + 1 : 1
    return acc
  }, {} as { [key: string]: number })

  const data = Object.values(stateCounts)
  const states = Object.keys(stateCounts)
  const colors = states.map((state) => theme?.colors?.[BIKE_MARKER_COLORS[state]]["400"] || "gray.400")

  const pieData = d3.pie<number>().value((d) => d)(data)

  const createArc = d3.arc().innerRadius(CLUSTER_MARKER_INNER_RADIUS).outerRadius(CLUSTER_MARKER_OUTER_RADIUS)

  const paths = pieData.map((d, i) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const arc = createArc(d)
    return `
      <g key="${i}">
        <path
          d="${arc}"
          fill="${colors[i]}"
        />
      </g>
    `
  })

  // create svg as string
  const svg = `
    <svg 
      xmlns="http://www.w3.org/2000/svg"
      width="${CLUSTER_MARKER_PIE_SIZE}px" 
      height="${CLUSTER_MARKER_PIE_SIZE}px"
    >
      <circle 
        cx="${CLUSTER_MARKER_OUTER_RADIUS}" 
        cy="${CLUSTER_MARKER_OUTER_RADIUS}" 
        fill="${getStateModeColor(stateCounts, theme)}" 
        r="${CLUSTER_MARKER_INNER_RADIUS}"
      />
      <g transform="translate(${CLUSTER_MARKER_OUTER_RADIUS} ${CLUSTER_MARKER_OUTER_RADIUS})">
        ${paths}
      </g>
    </svg>
  `

  // create marker using svg icon
  return new google.maps.Marker({
    position,
    icon: {
      url: `data:image/svg+xml;base64,${window.btoa(svg)}`, // base64 svg URL
      // scaledSize: new google.maps.Size(75, 75),
    },
    label: {
      text: String(count),
      color: "#fff",
      fontSize: "12px",
    },
    // adjust zIndex to be above other markers
    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
  })
}
